import api from './api';

export interface Discussion {
    id: string;
    user_id: string;
    user_name: string;
    username?: string;
    user_profile_picture?: string;
    type: 'bug' | 'feature' | 'question' | 'feedback' | 'astrology' | 'general' | 'announcement';
    types: ('bug' | 'feature' | 'question' | 'feedback' | 'astrology' | 'general' | 'announcement')[];  // Array of types
    title?: string;
    content: string;
    category?: string;
    priority?: number;
    featured_image?: string;
    image_filename?: string;
    upvote_count: number;
    downvote_count: number;
    upvotes?: string[];
    downvotes?: string[];
    comment_count: number;
    admin_hearted: boolean;
    admin_hearted_at?: string;
    pinned: boolean;
    pinned_at?: string;
    created_at: string;
    updated_at: string;
    comments?: DiscussionComment[];
}

export interface DiscussionComment {
    comment_id: string;
    user_id: string;
    user_name: string;
    username?: string;
    user_profile_picture?: string;
    content: string;
    image_filename?: string;
    upvote_count: number;
    downvote_count: number;
    upvotes?: string[];
    downvotes?: string[];
    created_at: string;
}

export interface DiscussionsResponse {
    success: boolean;
    discussions: Discussion[];
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
}

export interface DiscussionResponse {
    success: boolean;
    discussion: Discussion;
}

export const discussionService = {
    // Check if user has already claimed the first_discussion reward (requires login)
    // This checks RewardTracking which persists even if user deletes discussions/account
    checkRewardClaimed: async (): Promise<{ success: boolean; has_claimed: boolean; phone_verified?: boolean; reward_amount?: number }> => {
        const response = await api.get('/api/discussions/check-reward-claimed');
        return response.data;
    },

    // Get all discussions (public)
    getDiscussions: async (page: number = 1, per_page: number = 20, type: string = 'all'): Promise<DiscussionsResponse> => {
        const params = new URLSearchParams({
            page: page.toString(),
            per_page: per_page.toString(),
        });
        if (type && type !== 'all') {
            params.append('type', type);
        }
        const response = await api.get(`/api/discussions?${params.toString()}`);
        return response.data;
    },

    // Get single discussion (public)
    getDiscussion: async (id: string): Promise<DiscussionResponse> => {
        const response = await api.get(`/api/discussions/${id}`);
        return response.data;
    },

    // Create discussion (requires login)
    createDiscussion: async (data: {
        types: string[];  // Array of types
        title?: string;
        content: string;
        image?: File;
    }): Promise<{ success: boolean; message: string; discussion_id: string; is_first_discussion: boolean; new_balance: number | null }> => {
        const formData = new FormData();
        formData.append('types', JSON.stringify(data.types));  // Send as JSON string
        if (data.title) {
            formData.append('title', data.title);
        }
        formData.append('content', data.content);
        if (data.image) {
            formData.append('image', data.image);
        }

        const response = await api.post('/api/discussions', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    // Add comment (requires login)
    addComment: async (discussionId: string, data: {
        content: string;
        image?: File;
    }): Promise<{ success: boolean; message: string; comment_id: string }> => {
        const formData = new FormData();
        formData.append('content', data.content);
        if (data.image) {
            formData.append('image', data.image);
        }

        const response = await api.post(`/api/discussions/${discussionId}/comments`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    // Upvote discussion (requires login)
    upvoteDiscussion: async (id: string): Promise<{ success: boolean; action: string; upvote_count: number; downvote_count: number }> => {
        const response = await api.post(`/api/discussions/${id}/upvote`);
        return response.data;
    },

    // Downvote discussion (requires login)
    downvoteDiscussion: async (id: string): Promise<{ success: boolean; action: string; upvote_count: number; downvote_count: number }> => {
        const response = await api.post(`/api/discussions/${id}/downvote`);
        return response.data;
    },

    // Upvote comment (requires login)
    upvoteComment: async (discussionId: string, commentId: string): Promise<{ success: boolean; action: string; upvote_count: number; downvote_count: number }> => {
        const response = await api.post(`/api/discussions/${discussionId}/comments/${commentId}/upvote`);
        return response.data;
    },

    // Downvote comment (requires login)
    downvoteComment: async (discussionId: string, commentId: string): Promise<{ success: boolean; action: string; upvote_count: number; downvote_count: number }> => {
        const response = await api.post(`/api/discussions/${discussionId}/comments/${commentId}/downvote`);
        return response.data;
    },

    // Toggle admin heart (requires admin)
    toggleAdminHeart: async (id: string): Promise<{ success: boolean; action: string; admin_hearted: boolean }> => {
        const response = await api.post(`/api/discussions/${id}/heart`);
        return response.data;
    },

    // Toggle pin (requires admin)
    togglePin: async (id: string): Promise<{ success: boolean; action: string; pinned: boolean }> => {
        const response = await api.post(`/api/discussions/${id}/pin`);
        return response.data;
    },

    // Trigger bot response manually (requires admin)
    triggerBotResponse: async (id: string, adminInstruction?: string): Promise<{ success: boolean; message: string }> => {
        const payload = adminInstruction?.trim()
            ? { admin_instruction: adminInstruction.trim() }
            : {};
        const response = await api.post(`/api/discussions/${id}/trigger-bot`, payload);
        return response.data;
    },

    // Trigger bot response for a comment manually (requires admin)
    triggerBotResponseForComment: async (discussionId: string, commentId: string, adminInstruction?: string): Promise<{ success: boolean; message: string }> => {
        const payload = adminInstruction?.trim()
            ? { admin_instruction: adminInstruction.trim() }
            : {};
        const response = await api.post(`/api/discussions/${discussionId}/comments/${commentId}/trigger-bot`, payload);
        return response.data;
    },

    // Delete discussion (requires login, owner or admin)
    deleteDiscussion: async (id: string): Promise<{ success: boolean; message: string }> => {
        const response = await api.delete(`/api/discussions/${id}`);
        return response.data;
    },

    // Delete comment (requires login, owner or admin)
    deleteComment: async (discussionId: string, commentId: string): Promise<{ success: boolean; message: string }> => {
        const response = await api.delete(`/api/discussions/${discussionId}/comments/${commentId}`);
        return response.data;
    },

    // Edit discussion (requires login, owner or admin)
    editDiscussion: async (id: string, data: {
        types: string[];  // Array of types
        content: string;
    }): Promise<{ success: boolean; message: string }> => {
        const formData = new FormData();
        formData.append('types', JSON.stringify(data.types));  // Send as JSON string
        formData.append('content', data.content);

        const response = await api.put(`/api/discussions/${id}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    // Edit comment (requires login, owner or admin)
    editComment: async (discussionId: string, commentId: string, content: string): Promise<{ success: boolean; message: string }> => {
        const formData = new FormData();
        formData.append('content', content);

        const response = await api.put(`/api/discussions/${discussionId}/comments/${commentId}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    // Get presigned image URL
    getImageUrl: async (filename?: string, userId?: string): Promise<string | undefined> => {
        if (!filename || !userId) return undefined;

        try {
            const response = await api.get(`/api/discussions/image/${userId}/${filename}`);
            return response.data.url;
        } catch (error) {
            console.error('Failed to get image URL:', error);
            return undefined;
        }
    },
};
